<template>
  <v-dialog :value="value" max-width="960" scrollable :fullscreen="$vuetify.breakpoint.xsOnly" @input="$emit('input', $event)">
    <v-card class="rowform" :class="`tone-${tone}`" role="form" :aria-label="title">
      <div class="rowform__head">
        <span class="rowform__icon"><v-icon large>{{ failed ? "mdi-alert-circle-outline" : "mdi-check-circle-outline" }}</v-icon></span>
        <div>
          <h3 class="text-h6 rowform__title">{{ title }}</h3>
          <p class="text-body-2 rowform__muted mb-0" :title="subtitle">{{ subtitle }}</p>
        </div>
      </div>

      <v-card-text class="rowform__body">
        <!-- Estado de la fila con lo que hay en el formulario; cada error lleva al campo que hay que corregir. -->
        <div class="rowform__status" role="status">
          <span class="rowform__pill">{{ result.status }}</span>
          <span>{{ summary }}</span>
        </div>
        <ul v-if="errorList.length" class="rowform__errors" aria-label="Errores por corregir">
          <li v-for="item in errorList" :key="item.key">
            <button type="button" class="rowform__error" @click="focusField(item.key)"><strong>{{ item.caption }}:</strong> {{ item.message }}</button>
          </li>
        </ul>

        <section v-for="group in shown" :key="group.title" class="rowform__group">
          <h4 class="rowform__group-title">{{ group.title }}</h4>
          <div class="rowform__grid">
            <template v-for="field in group.fields">
              <v-autocomplete
                v-if="lists[field.key]"
                :key="field.key"
                :ref="`field-${field.key}`"
                v-model="draft[field.key]"
                :items="lists[field.key]"
                :label="field.caption"
                :error-messages="result.errors[field.key]"
                :no-data-text="lists[field.key].length ? 'Sin coincidencias' : 'No hay opciones: complete antes el dato del que depende.'"
                clearable
                dense
                outlined
                @change="revalidate"
              ></v-autocomplete>
              <v-text-field
                v-else
                :key="field.key"
                :ref="`field-${field.key}`"
                v-model="draft[field.key]"
                :label="field.caption"
                :error-messages="result.errors[field.key]"
                autocomplete="off"
                dense
                outlined
                @change="revalidate"
              ></v-text-field>
            </template>
          </div>
        </section>
      </v-card-text>

      <v-card-actions class="rowform__actions">
        <v-btn v-if="removeLabel" text color="red darken-3" @click="$emit('remove')"><v-icon left>mdi-delete-outline</v-icon>{{ removeLabel }}</v-btn>
        <v-spacer></v-spacer>
        <v-btn text @click="$emit('input', false)">Cancelar</v-btn>
        <v-btn v-if="hasNext" text color="blue darken-2" @click="$emit('apply-next', changes)">Aplicar y siguiente con errores</v-btn>
        <v-btn color="blue darken-2" class="white--text" @click="$emit('apply', changes)">Aplicar</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
// Intencional: el sanitizer reemplaza los caracteres de control del Excel por espacio.
// eslint-disable-next-line no-control-regex
const CONTROL_CHARS = /[\u0000-\u001F\u007F]+/g;
// Texto limpio (null es ""). Un número (el ID de un catálogo) queda como está: no se convierten tipos.
const tidy = (value) => (typeof value === "number" ? value : String(value ?? "").replace(CONTROL_CHARS, " ").trim());
// Una opción de lista es un texto o un objeto { text, value }: su valor.
const optionValue = (item) => (typeof item === "object" ? item.value : item);

// Formulario modal para corregir una fila de una importación V2 sin recorrer la tabla. Solo presenta: qué pasa al evaluar y al aplicar lo decide la pantalla.
// Trabaja sobre una copia (borrador) de los campos de la fila: Cancelar no cambia nada y Aplicar emite solo los campos que cambiaron.
// row: los valores iniciales del borrador (una fila) · fields: [{ key, caption }] · groups: [{ title, keys }]
// itemsFor(campo, borrador): opciones (textos u objetos { text, value }), o null si es texto libre
// validate(borrador, cambios): { errors: { campo: mensaje }, status, detail } · hasNext: hay otra fila con errores después de esta.
// removeLabel: si viene, botón para quitar la fila de la importación. Eventos: input (cerrar), apply(cambios), apply-next(cambios), remove.
export default {
  name: "ImportRowDialog",
  props: {
    value: { type: Boolean, default: false },
    title: { type: String, required: true },
    subtitle: { type: String, default: "" },
    row: { type: Object, default: null },
    fields: { type: Array, required: true },
    groups: { type: Array, required: true },
    itemsFor: { type: Function, default: () => null },
    validate: { type: Function, required: true },
    hasNext: { type: Boolean, default: false },
    removeLabel: { type: String, default: "" },
  },
  data() {
    return { draft: {}, result: { errors: {}, status: "", detail: "" } };
  },
  computed: {
    // La fila que se corrige mientras el diálogo está abierto: cambia al abrir y al pasar a otra fila.
    openRow() {
      return this.value ? this.row : null;
    },
    // Lo que cambió el usuario respecto de la fila, con el texto limpio y el ID tal cual (vaciar un desplegable deja null: es "").
    changes() {
      return Object.fromEntries(Object.entries(this.draft).filter(([key, value]) => tidy(value) !== tidy(this.row[key])).map(([key, value]) => [key, tidy(value)]));
    },
    // Opciones de cada campo según el borrador (cambiar el país o el departamento cambia las de departamento y ciudad). Un valor que no está en la lista se agrega para que se vea.
    lists() {
      return Object.fromEntries(this.fields.map((field) => {
        const items = this.itemsFor(field, this.draft);
        const current = this.draft[field.key];
        if (!items || !current || items.some((item) => optionValue(item) === current)) return [field.key, items];
        return [field.key, [items.some((item) => typeof item === "object") ? { text: String(current), value: current } : current, ...items]];
      }));
    },
    shown() {
      return this.groups.map(({ title, keys }) => ({ title, fields: keys.map((key) => this.fields.find((field) => field.key === key)).filter(Boolean) }));
    },
    // Errores en el orden en que se ven los campos.
    errorList() {
      return this.shown.flatMap((group) => group.fields).filter(({ key }) => this.result.errors[key])
        .map(({ key, caption }) => ({ key, caption: caption.replace("*", ""), message: this.result.errors[key] }));
    },
    failed() {
      return Object.keys(this.result.errors).length > 0;
    },
    tone() {
      return this.failed ? "danger" : "success";
    },
    summary() {
      const count = this.errorList.length;
      return count ? `${count.toLocaleString()} ${count === 1 ? "error" : "errores"} por corregir` : this.result.detail || "Sin errores";
    },
  },
  watch: {
    openRow: { immediate: true, handler(row) { if (row) this.start(); } },
  },
  methods: {
    // Al abrir o pasar a otra fila: borrador nuevo, su evaluación y el foco en el primer campo con error (después de que Vuetify enfoque el diálogo).
    start() {
      this.draft = Object.fromEntries(this.fields.map(({ key }) => [key, tidy(this.row[key])]));
      this.revalidate();
      setTimeout(() => this.focusField((this.errorList[0] || {}).key), 0);
    },
    // Con cada campo que cambia (no con cada tecla): el estado y los errores salen de la evaluación de la pantalla.
    revalidate() {
      this.result = this.validate({ ...this.draft }, this.changes);
    },
    focusField(key) {
      const [field] = this.$refs[`field-${key}`] || [];
      if (field) field.focus();
    },
  },
};
</script>

<style scoped>
.rowform {
  --tone-bg: #e6f6ec;
  --tone-ink: #14532d;
  display: flex;
  flex-direction: column;
}

.rowform.tone-danger {
  --tone-bg: #fdecea;
  --tone-ink: #922b21;
}

.rowform__head {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 24px 24px 16px;
}

.rowform__icon {
  display: grid;
  flex: 0 0 auto;
  place-items: center;
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background-color: var(--tone-bg);
}

.rowform__icon .v-icon {
  color: var(--tone-ink) !important;
}

.rowform__title {
  margin: 0;
  color: rgba(0, 0, 0, 0.87);
}

/* El nombre del proveedor puede ser larguísimo y sin espacios: se parte y se recorta a 2 líneas (completo en el tooltip). */
.rowform__head > div {
  min-width: 0;
}

.rowform__muted {
  display: -webkit-box;
  overflow: hidden;
  overflow-wrap: anywhere;
  color: rgba(0, 0, 0, 0.65);
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}

.rowform__body {
  padding: 0 24px 16px !important;
}

.rowform__status {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 12px;
  margin-bottom: 12px;
  color: rgba(0, 0, 0, 0.87);
  font-size: 0.875rem;
}

.rowform__pill {
  padding: 4px 12px;
  border-radius: 999px;
  background-color: var(--tone-bg);
  color: var(--tone-ink);
  font-weight: 600;
}

.rowform__errors {
  margin: 0 0 16px;
  padding: 10px 14px;
  border-radius: 8px;
  background-color: #fdecea;
  list-style: none;
  font-size: 0.875rem;
}

.rowform__error {
  padding: 2px 0;
  color: #922b21;
  font: inherit;
  text-align: left;
  text-decoration: underline;
  cursor: pointer;
}

.rowform__group-title {
  margin: 8px 0 12px;
  color: rgba(0, 0, 0, 0.65);
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

/* 3 columnas en escritorio, 2 en tableta y 1 en móvil: el ancho mínimo de cada campo decide. */
.rowform__grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 10px 16px;
}

/* Un error largo ocupa dos líneas: con aire entre ellas y sin pegarse al campo de abajo. */
.rowform__grid ::v-deep .v-messages__message {
  line-height: 1.35;
}

.rowform__actions {
  flex-wrap: wrap;
  gap: 8px;
  padding: 12px 24px 20px;
  border-top: 1px solid rgba(0, 0, 0, 0.08);
}
</style>

<style>
/* Lo que abre este formulario desde la tabla (chip de Estado, texto de Detalle) lleva la clase row-opener. Sin scoped: la tabla lo dibuja fuera de este componente. */
.row-opener {
  cursor: pointer;
}

span.row-opener {
  text-decoration: underline dotted;
  text-underline-offset: 3px;
}

.row-opener:focus-visible {
  outline: 2px solid #1565c0;
  outline-offset: 2px;
}
</style>
